import { useEffect, useRef, useState } from 'react';

interface StatusBarProps {
  online: boolean;
  syncing: boolean;
  serverReachable: boolean;
  queuedCount: number;
  skewWarn: boolean;
  skewMs: number | null;
}

/**
 * How long the raw offline condition (`!online || !serverReachable`) must PERSIST
 * before the badge shows "Offline". A punch fires the camera, which on iOS can
 * briefly flap navigator.onLine (and a lone probe can miss) — without this grace
 * the badge flickered Online→Offline→Online on every punch. Any success clears it
 * to Online immediately, so a real outage still shows within a few seconds while a
 * transient blip never does.
 */
const OFFLINE_GRACE_MS = 6_000;
/**
 * How long a sync must be in flight before "Syncing…" shows. A single punch syncs
 * in well under this, so its brief flash is suppressed (the punch echo card already
 * confirms the punch); only a real backlog drain surfaces the badge.
 */
const SYNCING_GRACE_MS = 1_200;

/**
 * Debounce a boolean so it turns TRUE only after `raw` has held true for `delayMs`,
 * and turns FALSE immediately when `raw` goes false. Used to keep the connectivity
 * + syncing badges from flickering on the transient blips a punch causes.
 */
function useDebouncedTrue(raw: boolean, delayMs: number): boolean {
  const [value, setValue] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!raw) {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
      setValue(false);
      return;
    }
    timer.current = window.setTimeout(() => {
      setValue(true);
      timer.current = null;
    }, delayMs);
    return () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [raw, delayMs]);

  return value;
}

/**
 * Compact status row: connectivity, in-flight sync, queued-punch count, and a
 * soft clock-skew warning. Everything here is informational — the clock stays
 * fully usable regardless of what these show. The connectivity + syncing badges
 * are debounced so a punch's transient blips never flicker the display.
 */
export function StatusBar({
  online,
  syncing,
  serverReachable,
  queuedCount,
  skewWarn,
  skewMs,
}: StatusBarProps): JSX.Element {
  const offline = useDebouncedTrue(!online || !serverReachable, OFFLINE_GRACE_MS);
  const showSyncing = useDebouncedTrue(syncing, SYNCING_GRACE_MS);
  const skewMinutes = skewMs === null ? 0 : Math.round(skewMs / 60_000);

  return (
    <div className="statusbar" role="status">
      {offline ? (
        <span className="badge badge--offline">Offline — punches are saved</span>
      ) : (
        <span className="badge">Online</span>
      )}
      {showSyncing && <span className="badge badge--syncing">Syncing…</span>}
      {queuedCount > 0 && <span className="badge badge--queued">{queuedCount} queued</span>}
      {skewWarn && (
        <span className="badge badge--skew">
          Clock off by ~{Math.abs(skewMinutes)} min — check device time
        </span>
      )}
    </div>
  );
}
